import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import CardSectionHeader from '~/Nova/scenes/Site/components/CardSectionHeader';
import EmptySectionCard from '~/Nova/scenes/Site/components/EmptySectionCard';

import CreateSnapshotModal from './components/CreateSnapshotModal';

interface SnapshotsProps {
  used?: number;
  max?: number;
  onCreateSnapshot?: (label: string) => void;
}

const Snapshots = ({
  used = 0,
  max = 2,
  onCreateSnapshot = () => {},
}: SnapshotsProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'snapshots',
  });
  const theme = useTheme();
  const [isCreateSnapshotModalOpen, setIsCreateSnapshotModalOpen] =
    useState(false);

  const isEmpty = used === 0;

  const handleOpenModal = () => setIsCreateSnapshotModalOpen(true);
  const handleCloseModal = () => setIsCreateSnapshotModalOpen(false);

  const handleSubmit = (label: string) => {
    onCreateSnapshot?.(label);
    setIsCreateSnapshotModalOpen(false);
  };

  return (
    <Stack spacing={2} data-testid="snapshots-section">
      <CardSectionHeader
        title={t('snapshotTableHeading', { used, max })}
        marginBottom={0}
        action={
          <Button
            variant="contained"
            fullWidth
            onClick={handleOpenModal}
            data-testid="create-snapshot-btn"
          >
            {t('createBtn')}
          </Button>
        }
      />

      {isEmpty && (
        <EmptySectionCard>
          <Stack alignItems="center" spacing={1.5}>
            <IconButton
              disableRipple
              sx={{
                backgroundColor: theme.palette.primary.light ?? '#e8f0fe',
                width: 64,
                height: 64,
                borderRadius: '50%',
              }}
            >
              <AddBoxOutlinedIcon
                sx={{ fontSize: 32, color: theme.palette.primary.main }}
              />
            </IconButton>

            <Typography variant="h3" data-testid="snapshots-empty-title">
              {t('emptyState.heading')}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 400 }}
              data-testid="snapshots-empty-description"
            >
              {t('emptyState.description')}
            </Typography>

            <Button
              variant="contained"
              onClick={handleOpenModal}
              data-testid="create-first-snapshot-btn"
              sx={{ mt: 1 }}
            >
              {t('emptyState.createBtn')}
            </Button>
          </Stack>
        </EmptySectionCard>
      )}

      <CreateSnapshotModal
        open={isCreateSnapshotModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
      />
    </Stack>
  );
};

export default Snapshots;
